import React, { useCallback, useMemo } from "react";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "antd/es/select";
import { ColumnType } from "antd/es/table";
import { FormattedMessage, IntlShape } from "react-intl";

import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import {
  EntityType,
  EntityTypesSupportedByIntegrations,
} from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import { Alert } from "@mapmycustomers/ui";

import { CreateNewCustomField } from "@app/store/integrations";
import Integration from "@app/types/integrations/Integration";
import IntegrationField from "@app/types/integrations/IntegrationField";
import { isCalculatedField, isUniqueCustomField } from "@app/util/fieldModel/impl/assert";
import { getFieldCategoryDisplayName } from "@app/util/ui";

import { getEntityRequiredFieldNamesForIntegration } from "../../utils/getEntityRequiredFieldsForIntegration";
import MappingInformation from "../MappingInformation";

import { ChangeGrouping } from "./ChangeGrouping";
import styles from "./FieldMappingTable.module.scss";
import messages from "./messages";

const useMappingTableColumnConfig = (
  intl: IntlShape,
  entityType: EntityTypesSupportedByIntegrations,
  fieldModel: IFieldModel,
  fieldCategories: Partial<{ [key in FieldCategory]: IField[] }>,
  serviceName: string,
  usedFieldNames: Set<string>,
  onSearch: (value: string) => void,
  onShowMmcFieldInfoModal: () => void,
  onShowGroupFieldInfoModal: () => void,
  onChangeMapping: (mapping: {
    entityType: EntityTypesSupportedByIntegrations;
    field: IField | undefined;
    integrationField: IntegrationField;
    isCustomField: boolean;
  }) => void,
  onChangeGrouping: (grouping: {
    enabled: boolean;
    entityType: EntityTypesSupportedByIntegrations;
    integrationField: IntegrationField;
  }) => void,
  currentIntegration?: Integration
): ColumnType<IntegrationField>[] => {
  const incomingIntegration = currentIntegration?.syncOptions[entityType].incoming;
  const outgoingIntegration = currentIntegration?.syncOptions[entityType].outgoing;
  const requiredFieldNames = useMemo(
    () => getEntityRequiredFieldNamesForIntegration(entityType, currentIntegration?.type),
    [entityType, currentIntegration]
  );

  const formatDisplayName = useCallback(
    (field: IField) => `${field.displayName}${requiredFieldNames.has(field.name) ? "*" : ""}`,
    [requiredFieldNames]
  );

  return useMemo(() => {
    const handleFieldChange = (integrationField: IntegrationField, mmcFieldName: string) => {
      onChangeMapping({
        entityType,
        field:
          mmcFieldName === CreateNewCustomField ? undefined : fieldModel.getByName(mmcFieldName),
        integrationField,
        isCustomField: mmcFieldName === CreateNewCustomField,
      });
      onSearch("");
    };

    const columns: ColumnType<IntegrationField>[] = [
      {
        dataIndex: "crmDisplayName",
        key: "crmDisplayName",
        render: (crmDisplayName: string, integrationField: IntegrationField) => {
          const field = integrationField.mmcField
            ? fieldModel.getByName(integrationField.mmcField)
            : undefined;
          const isMmcFieldUnique = field ? isUniqueCustomField(field) : false;
          return (
            <>
              {crmDisplayName}
              {isMmcFieldUnique && !integrationField.isUnique && (
                <Alert
                  message={intl.formatMessage(messages.uniqueAlert, { b: (text) => <b>{text}</b> })}
                  showIcon
                  type="warning"
                />
              )}
            </>
          );
        },
        title: intl.formatMessage(messages.crmDisplayName, { serviceName }),
      },
      {
        dataIndex: "mmcField",
        key: "mmcField",
        render: (mappedField: null | string, integrationField: IntegrationField) => {
          const field = mappedField ? fieldModel.getByName(mappedField) : undefined;
          const isMmcFieldUnique = field ? isUniqueCustomField(field) : false;
          return (
            <div className={styles.mmcField}>
              <Select
                className={styles.fieldSelect}
                dropdownMatchSelectWidth={false}
                filterOption={false}
                onChange={(fieldName: string) => handleFieldChange(integrationField, fieldName)}
                onSearch={onSearch}
                showSearch
                value={mappedField || ""}
              >
                <Select.Option key={0} value="">
                  <FormattedMessage
                    id="integrations.fieldMappingTable.column.mmcField.none"
                    defaultMessage="None"
                    description="Text of None snippet"
                  />
                </Select.Option>

                {(Object.keys(fieldCategories) as FieldCategory[]).map((category) =>
                  fieldCategories[category]!.length ? (
                    <Select.OptGroup
                      key={category}
                      label={getFieldCategoryDisplayName(intl, category)}
                    >
                      {fieldCategories[category]!.filter(
                        (field) => outgoingIntegration || !isCalculatedField(field)
                      ).map((field) => (
                        <Select.Option
                          disabled={usedFieldNames.has(field.name)}
                          key={field.name}
                          value={field.name}
                        >
                          {formatDisplayName(field)}
                        </Select.Option>
                      ))}
                    </Select.OptGroup>
                  ) : null
                )}

                <Select.Option key={CreateNewCustomField} value={CreateNewCustomField}>
                  <FormattedMessage
                    id="integrations.fieldMappingTable.column.mmcField.createNewCustomField"
                    defaultMessage="Create new custom field"
                    description="Text of Create new custom field link"
                  />
                </Select.Option>
              </Select>
              {mappedField &&
                fieldModel.getByName(mappedField)?.hasFeature(FieldFeature.TERRITORY_FIELD) && (
                  <Alert
                    message={intl.formatMessage(messages.territoryWarning)}
                    showIcon
                    type="warning"
                  />
                )}
              {mappedField &&
                outgoingIntegration &&
                incomingIntegration &&
                fieldModel.getByName(mappedField) &&
                isCalculatedField(fieldModel.getByName(mappedField)!) && (
                  <Alert
                    message={intl.formatMessage(messages.calculatedFieldWarning)}
                    showIcon
                    type="warning"
                  />
                )}
              {!isMmcFieldUnique && integrationField.isUnique && (
                <Alert
                  message={intl.formatMessage(messages.uniqueAlert, { b: (text) => <b>{text}</b> })}
                  showIcon
                  type="warning"
                />
              )}
            </div>
          );
        },
        title: intl.formatMessage(messages.mmcField, {
          infoIcon: (
            <FontAwesomeIcon
              className={styles.infoIcon}
              icon={faInfoCircle}
              onClick={onShowMmcFieldInfoModal}
            />
          ),
        }),
      },
    ];

    columns.push({
      dataIndex: "mmcField",
      key: "syncing",
      render: (mappedField: null | string) => <MappingInformation mapped={!!mappedField} />,
      title: intl.formatMessage(messages.syncing),
    });

    // we don't support grouping for activities yet
    if (entityType === EntityType.ACTIVITY) {
      return columns;
    }

    columns.push({
      dataIndex: "mmcGroupField",
      key: "mmcGroupField",
      render: (mmcGroupField: null | string, integrationField: IntegrationField) => (
        <ChangeGrouping
          disabled={!integrationField.mmcField}
          mmcGroupField={mmcGroupField}
          onChangeGrouping={(enabled: boolean) =>
            onChangeGrouping({ enabled, entityType, integrationField })
          }
          onShowGroupFieldInfoModal={onShowGroupFieldInfoModal}
        />
      ),
      title: intl.formatMessage(messages.mmcGroupField, {
        infoIcon: (
          <FontAwesomeIcon
            className={styles.infoIcon}
            icon={faInfoCircle}
            onClick={onShowGroupFieldInfoModal}
          />
        ),
      }),
    });

    return columns;
  }, [
    entityType,
    fieldCategories,
    fieldModel,
    formatDisplayName,
    incomingIntegration,
    intl,
    onChangeGrouping,
    onChangeMapping,
    onSearch,
    onShowGroupFieldInfoModal,
    onShowMmcFieldInfoModal,
    outgoingIntegration,
    serviceName,
    usedFieldNames,
  ]);
};

export default useMappingTableColumnConfig;
